import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, RefreshCw, KeyRound, Copy, Check, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminApiKeysPage() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', scope: 'external_logistics' });
  const [creating, setCreating] = useState(false);

  // Reveal-once dialog (shown right after a successful create)
  const [revealedKey, setRevealedKey] = useState(null); // { name, api_key }
  const [copied, setCopied] = useState(false);

  const fetchKeys = useCallback(async () => {
    const t = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/api-keys`, { headers: { Authorization: `Bearer ${t}` } });
      setKeys(res.data.keys || []);
    } catch (err) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleAdd = async () => {
    if (!addForm.name.trim()) { toast.error('A name is required (e.g. "emergent-logistics")'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/api-keys`, addForm, { headers });
      setShowAdd(false);
      setAddForm({ name: '', scope: 'external_logistics' });
      setRevealedKey({ name: res.data.name, api_key: res.data.api_key });
      setCopied(false);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke "${key.name}"? Any integration using this key will immediately stop working.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/api-keys/${key.id}`, { headers });
      toast.success('Key revoked');
      fetchKeys();
    } catch (err) {
      toast.error('Revoke failed');
    }
  };

  const handleCopy = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey.api_key);
      setCopied(true);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-api-keys-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-sm text-gray-500 mt-1">
            Keys for external integrations (e.g. the Emergent logistics module) to call the portal's API.
            Separate from your admin login — a key only unlocks the narrow <code>/api/external/*</code> routes.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-api-key-btn">
          <Plus className="h-4 w-4 mr-1" /> New Key
        </Button>
      </div>

      {/* Keys Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> Issued Keys</CardTitle>
          <CardDescription>{keys.length} key{keys.length === 1 ? '' : 's'}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Scope</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                    <TableHead className="text-xs">Last Used</TableHead>
                    <TableHead className="text-xs w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map(k => (
                    <TableRow key={k.id} className="text-sm hover:bg-gray-50">
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="text-xs text-gray-600">{k.scope}</TableCell>
                      <TableCell>
                        {k.is_active ? (
                          <Badge className="bg-green-50 text-green-700 text-xs font-medium">Active</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-500 text-xs font-medium">Revoked</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{formatDate(k.created_at)}</TableCell>
                      <TableCell className="text-xs text-gray-500">{formatDate(k.last_used_at)}</TableCell>
                      <TableCell>
                        {k.is_active && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-gray-500 hover:text-red-600"
                            onClick={() => handleRevoke(k)}
                            data-testid={`revoke-api-key-${k.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {keys.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No API keys yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-api-key-dialog">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>The raw key is shown once, immediately after creation. It cannot be retrieved again — only revoked and replaced.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Name *</label>
              <Input
                value={addForm.name}
                onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. emergent-logistics"
                data-testid="add-api-key-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Scope</label>
              <Input
                value={addForm.scope}
                onChange={e => setAddForm(p => ({ ...p, scope: e.target.value }))}
                placeholder="external_logistics"
              />
              <p className="text-xs text-gray-400 mt-1">Which API surface this key unlocks. Leave as-is for the logistics integration.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={creating} data-testid="add-api-key-submit">
              {creating ? <><RefreshCw className="h-4 w-4 animate-spin mr-1" /> Creating...</> : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal-once Dialog */}
      <Dialog open={!!revealedKey} onOpenChange={(open) => { if (!open) setRevealedKey(null); }}>
        <DialogContent data-testid="reveal-api-key-dialog">
          <DialogHeader>
            <DialogTitle>Key created: {revealedKey?.name}</DialogTitle>
            <DialogDescription className="flex items-start gap-1.5 text-amber-600">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              Copy this now — it will not be shown again. Send it to the integration owner over a secure channel, not plaintext email or chat.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-50 border rounded px-3 py-2 text-xs break-all">{revealedKey?.api_key}</code>
            <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-api-key-btn">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
